import {createContext, ReactNode, useCallback, useContext} from 'react';

import {useNavigate} from 'react-router-dom';

import {LINKS} from '@/constants/LinksConstants';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';

import {IKid} from '../../model/AddKidMode';
import {getKidData} from '../../services/handleKidInformation';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  kidData: Partial<IKid>;
  handleTryAgain: () => void;
  handleContactCS: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export const ViewModelProvider = ({children}: {children: ReactNode}) => {
  //#region [---- [PROPERTIES] ----]
  const kidData = getKidData();
  const navigate = useNavigate();
  const {trackSelectCreateTeenErrorRetry, trackSelectCreateTeenErrorCS} =
    useTrackers();
  //#endregion

  //#region [---- [LOGIC] ----]
  const handleTryAgain = () => {
    trackSelectCreateTeenErrorRetry(kidData?.uuid);
    navigate('/onboarding/add-kid/creating');
  };

  const handleContactCS = useCallback(() => {
    trackSelectCreateTeenErrorCS(kidData?.uuid);
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({status: WEB_VIEW_MESSAGES.HELP_CENTER}),
      );
      return;
    }

    window.open(LINKS.MODAK_SUPPORT, '_blank');
  }, [kidData?.uuid, trackSelectCreateTeenErrorCS]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{kidData, handleTryAgain, handleContactCS}}>
      {children}
    </ViewModelContext.Provider>
  );
};

export const useViewModelProvider = () => {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error('AddKidWarning ViewModelProvider is not found.');
  }
  return context;
};
